/**
 * A dependent info type's *direct* required ancestors. Built by
 * `useRollupKeyDependencies` from the live-list config registry; kept as a
 * plain data structure here so the graph traversal below has no React/query
 * dependency and is trivial to test.
 */
export type RollupKeyDependencyMap = Map<string, Set<string>>

function transitiveClosure(
  startId: string,
  edgesByInfoTypeId: RollupKeyDependencyMap,
): Set<string> {
  const visited = new Set<string>()
  const queue = [startId]
  while (queue.length > 0) {
    const current = queue.shift() as string
    const next = edgesByInfoTypeId.get(current)
    if (!next) continue
    next.forEach((id) => {
      if (visited.has(id)) return
      visited.add(id)
      queue.push(id)
    })
  }
  return visited
}

/** Every ancestor `infoTypeId` requires, directly or transitively. */
export function getRequiredAncestors(
  infoTypeId: string,
  requiredAncestorsByInfoTypeId: RollupKeyDependencyMap,
): Set<string> {
  return transitiveClosure(infoTypeId, requiredAncestorsByInfoTypeId)
}

/** Every info type that requires `infoTypeId`, directly or transitively —
 * the inverse of `requiredAncestorsByInfoTypeId`. Computed on demand rather
 * than cached alongside it: the dependency map is small (bounded by a
 * project's eligible key fields) and this only runs on a toggle click. */
export function getTransitiveDependents(
  infoTypeId: string,
  requiredAncestorsByInfoTypeId: RollupKeyDependencyMap,
): Set<string> {
  const dependentsByAncestorId: RollupKeyDependencyMap = new Map()
  requiredAncestorsByInfoTypeId.forEach((ancestors, dependentId) => {
    ancestors.forEach((ancestorId) => {
      let dependents = dependentsByAncestorId.get(ancestorId)
      if (!dependents) {
        dependents = new Set()
        dependentsByAncestorId.set(ancestorId, dependents)
      }
      dependents.add(dependentId)
    })
  })
  return transitiveClosure(infoTypeId, dependentsByAncestorId)
}

/**
 * Toggles `toggledId` in `currentIds`, maintaining the invariant that every
 * kept info type's required ancestors are also kept — the UI-side half of
 * ancestor-completeness (the export-time check is the other half, server/
 * relay-side, out of scope here).
 *
 * Turning a field ON auto-includes its required ancestors, transitively.
 * Turning a field OFF drops everything that depends on it, transitively —
 * otherwise a dependent could be left in the key with its required ancestor
 * missing, which is exactly the state the export-time check exists to catch.
 */
export function toggleRollupKeyInfoType(
  currentIds: string[],
  toggledId: string,
  requiredAncestorsByInfoTypeId: RollupKeyDependencyMap,
): string[] {
  const currentSet = new Set(currentIds)

  if (currentSet.has(toggledId)) {
    const toRemove = getTransitiveDependents(
      toggledId,
      requiredAncestorsByInfoTypeId,
    )
    toRemove.add(toggledId)
    return currentIds.filter((id) => !toRemove.has(id))
  }

  const toAdd = getRequiredAncestors(toggledId, requiredAncestorsByInfoTypeId)
  toAdd.add(toggledId)
  const next = [...currentSet]
  toAdd.forEach((id) => {
    if (!currentSet.has(id)) next.push(id)
  })
  return next
}
